import {
  NonNullableStringParameter,
  type SearchValues,
  useAssignQueryParameter,
  useWithDefault,
} from '@pba/react-router';
import { useCallback, useMemo } from 'react';
import type { ChatroomListType, ChatroomsListFilter } from 'features/chat';
import { ChatroomListTypeParameter } from '../chatroom-list-type-parameter';

export type ChatroomsPageFilterParams = {
  createFilterUrlUpdates: (filter: Partial<ChatroomsListFilter>) => SearchValues;
  filter: ChatroomsListFilter;
};

type P = {
  searchKey?: string;
  typeKey?: string;
};

export const useChatroomsPageFilterParams = ({
  searchKey = 'search',
  typeKey = 'type',
}: P = {}): ChatroomsPageFilterParams => {
  const { assignValue: setSearch, value: search } = useAssignQueryParameter(searchKey, NonNullableStringParameter);
  const { assignValue: setType, value: type } = useAssignQueryParameter(
    typeKey,
    useWithDefault<ChatroomListType>(ChatroomListTypeParameter, 'all'),
  );

  const createFilterUrlUpdates = useCallback(
    (filterUpdates: Partial<ChatroomsListFilter>) => {
      const urlUpdates: SearchValues = {};

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- casting with "as" on purpose — this limits iteration to ChatroomsListFilter keys and guarantees we handle each of them explicitly.
      for (const key of Object.keys(filterUpdates) as Array<keyof ChatroomsListFilter>) {
        switch (key) {
          case 'search':
            setSearch({ newValue: filterUpdates[key], object: urlUpdates });
            break;
          case 'type':
            setType({ newValue: filterUpdates[key], object: urlUpdates });
            break;
        }
      }

      return urlUpdates;
    },
    [setSearch, setType],
  );

  const filter = useMemo<ChatroomsListFilter>(
    () => ({
      search,
      type,
    }),
    [search, type],
  );

  return useMemo(
    () => ({
      createFilterUrlUpdates,
      filter,
    }),
    [createFilterUrlUpdates, filter],
  );
};
